<template>
  <div>
    <div class="d-flex align-center mb-4">
      <span class="text-subtitle-2 grey--text text--darken-1">
        Dirección principal para Relbase (máximo 1)
      </span>
    </div>

    <div v-if="!address && !editing">
      <v-btn
        v-if="$can('create', 'Address') && !readonly"
        color="primary"
        outlined
        small
        @click="startCreate"
      >
        <v-icon x-small class="mr-2">fas fa-plus</v-icon>
        Agregar Dirección
      </v-btn>
    </div>

    <div v-if="address && !editing">
      <v-simple-table dense>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Dirección</th>
            <th>Ciudad</th>
            <th>Comuna</th>
            <th class="text-end" width="60"></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ address.name }}</td>
            <td>{{ address.street }} {{ address.number }}</td>
            <td>{{ address.city }}</td>
            <td>{{ address.comune }}</td>
            <td class="text-end">
              <v-btn v-if="!readonly" icon x-small color="primary" @click="startEdit" class="mx-1">
                <v-icon x-small>fas fa-edit</v-icon>
              </v-btn>
              <v-icon v-else x-small color="grey" title="Sincronizado con Relbase — solo editable desde Relbase">mdi-lock</v-icon>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <v-dialog v-model="editing" max-width="550" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">fas fa-map-marked-alt</v-icon>
            <div>
              <div class="text-h6 font-weight-bold lh-1">
                {{ isNew ? 'Crear Dirección' : 'Editar Dirección' }}
              </div>
              <div class="caption grey--text mt-1">
                Dirección principal para sincronización con Relbase
              </div>
            </div>
          </div>
        </v-sheet>

        <v-card-text class="pa-6 pt-8">
          <ValidationObserver v-slot="{ handleSubmit }">
            <v-form @submit.prevent="handleSubmit(submit)">
              <v-row dense>
                <v-col cols="12">
                  <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.name"
                      label="Nombre / Etiqueta"
                      outlined dense
                      placeholder="Ej: Oficina Central"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider name="Región" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.region"
                      label="Región"
                      outlined dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider name="Ciudad" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="selectedCity"
                      :items="cities"
                      item-text="name"
                      item-value="relbase_id"
                      label="Ciudad"
                      outlined dense
                      :loading="loadingCities"
                      :error-messages="errors"
                      return-object
                      @change="onCityChange"
                      :search-input.sync="citySearch"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-title>
                            {{ loadingCities ? 'Cargando...' : 'No se encontraron ciudades' }}
                          </v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider name="Comuna" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="selectedCommune"
                      :items="communes"
                      item-text="name"
                      item-value="relbase_id"
                      label="Comuna"
                      outlined dense
                      :loading="loadingCommunes"
                      :disabled="!selectedCity"
                      :error-messages="errors"
                      return-object
                      :search-input.sync="communeSearch"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-title>
                            {{ loadingCommunes ? 'Cargando...' : (selectedCity ? 'No se encontraron comunas' : 'Seleccione una ciudad primero') }}
                          </v-list-item-title>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6">
                  <ValidationProvider name="Calle" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.street"
                      label="Calle"
                      outlined dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="4">
                  <ValidationProvider name="Número" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="form.number"
                      label="Número"
                      outlined dense
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="form.suite"
                    label="Referencia Adicional"
                    outlined dense
                    placeholder="Piso, Depto, Oficina"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-6"></v-divider>

              <div class="d-flex justify-end">
                <v-btn text small @click="cancel" class="mr-2" :disabled="loading">Cancelar</v-btn>
                <v-btn type="submit" color="primary" depressed small class="px-6" :loading="loading">
                  <v-icon x-small class="mr-2">fas fa-save</v-icon>
                  {{ isNew ? 'Registrar' : 'Actualizar' }}
                </v-btn>
              </div>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { createAddress, updateAddress } from '@/api/customers'
import { fetchRelbaseCities, fetchRelbaseCommunes } from '@/api/relbase'
import notifications from '@/utils/notifications'

export default {
  name: 'AddressRelbase',
  props: {
    customerId: { type: Number, required: true },
    value: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false }
  },
  data: () => ({
    editing: false,
    isNew: true,
    loading: false,
    cities: [],
    communes: [],
    loadingCities: false,
    loadingCommunes: false,
    selectedCity: null,
    selectedCommune: null,
    citySearch: '',
    communeSearch: '',
    form: {
      name: '',
      region: '',
      street: '',
      number: '',
      suite: ''
    }
  }),
  computed: {
    address() {
      return this.value && this.value.length > 0 ? this.value[0] : null
    }
  },
  async created() {
    await this.loadCities()
  },
  methods: {
    async loadCities() {
      this.loadingCities = true
      try {
        this.cities = await fetchRelbaseCities()
      } catch (e) {
        notifications.showError('Error cargando ciudades: ' + e)
      } finally {
        this.loadingCities = false
      }
    },
    async loadCommunes(cityRelbaseId) {
      this.loadingCommunes = true
      this.communes = []
      try {
        this.communes = await fetchRelbaseCommunes(cityRelbaseId)
      } catch (e) {
        notifications.showError('Error cargando comunas: ' + e)
      } finally {
        this.loadingCommunes = false
      }
    },
    onCityChange(city) {
      this.selectedCommune = null
      if (city && city.relbase_id) {
        this.loadCommunes(city.relbase_id)
      } else {
        this.communes = []
      }
    },
    startCreate() {
      this.isNew = true
      this.form = { name: '', region: '', street: '', number: '', suite: '' }
      this.selectedCity = null
      this.selectedCommune = null
      this.communes = []
      this.editing = true
    },
    async startEdit() {
      this.isNew = false
      this.form = {
        id: this.address.id,
        name: this.address.name,
        region: this.address.region,
        street: this.address.street,
        number: this.address.number,
        suite: this.address.suite
      }
      // Restore city selection from stored relbase_city_id
      if (this.address.relbase_city_id) {
        this.selectedCity = this.cities.find(c => c.relbase_id === this.address.relbase_city_id) || null
        if (this.selectedCity) {
          await this.loadCommunes(this.selectedCity.relbase_id)
          if (this.address.relbase_commune_id) {
            this.selectedCommune = this.communes.find(c => c.relbase_id === this.address.relbase_commune_id) || null
          }
        }
      } else {
        this.selectedCity = null
        this.selectedCommune = null
        this.communes = []
      }
      this.editing = true
    },
    async submit() {
      this.loading = true
      try {
        const data = {
          ...this.form,
          city: this.selectedCity ? this.selectedCity.name : '',
          comune: this.selectedCommune ? this.selectedCommune.name : '',
          relbase_city_id: this.selectedCity ? this.selectedCity.relbase_id : null,
          relbase_commune_id: this.selectedCommune ? this.selectedCommune.relbase_id : null,
          default: true,
          customer: this.customerId
        }

        if (this.isNew) {
          await createAddress(data)
          notifications.showSuccess('Dirección creada correctamente')
        } else {
          await updateAddress(data)
          notifications.showSuccess('Dirección actualizada correctamente')
        }
        this.$emit('updated')
        this.editing = false
      } catch (e) {
        notifications.showError('Error al guardar la dirección: ' + e)
      } finally {
        this.loading = false
      }
    },
    cancel() {
      this.editing = false
    }
  }
}
</script>

<style scoped>
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1.2; }
</style>
